import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MainTableFilterModel } from 'src/app/shared/models/main-table-filter.model';
import { ApiResponse } from 'src/app/core/models/api-response';
import { Observable } from 'rxjs';
import { getDepartmentId, getTenantId } from 'src/app/services/store-snapshot.service';
import { environment } from 'src/environments/environment';
import { Store } from '@ngxs/store';
import { MainTableFilteringHelperService } from 'src/app/shared/main-table-module/services/main-table-filtering-helper.service';
import { DatePipe } from '@angular/common';
import { IPersonnelOnBoard } from 'src/app/crew-management-user/models/current-personnel/personnel-on-board.model';
import { DEFAULT_DATE_FORMAT } from 'src/app/core/ccomx-date-formats';
import {
  IAlternativeWorkOverviewResponse,
  IWorkOverviewDetails,
} from '../models/work-overview-details.model';
import {
  IDailyCrewWorkOverviewResult,
  IGetDailyCrewWorkOverviewRequest,
} from '../models/daily-crew-work-overview.model';
import {
  IWorkOverviewMatrixRequest,
  IWorkOverviewMatrixRow,
} from '../models/work-overview-matrix.model';

const COLUMN_TO_KEY_MAP: Record<string, string> = {
  position: 'AssignedPosition',
  workSchedule: 'AssignedWorkSchedule',
};

@Injectable({
  providedIn: 'root',
})
export class WorkOverviewApiService {
  headers = { 'content-type': 'application/json' };

  private get url(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${getDepartmentId()}/WorkOverviews`;
  }

  constructor(
    private http: HttpClient,
    private store: Store,
    private datePipe: DatePipe
  ) {}

  getWorkOverviews(
    request: IWorkOverviewMatrixRequest,
    currentFilter?: MainTableFilterModel
  ): Observable<ApiResponse<IWorkOverviewMatrixRow[]>> {
    let params = new HttpParams();

    if (request.year !== undefined && request.month !== undefined) {
      params = params.set('Year', `${request.year}`);
      params = params.set('Month', `${request.month}`);
    }

    if (
      request.showOnlyCurrentPersonnel !== null &&
      request.showOnlyCurrentPersonnel !== undefined
    ) {
      params = params.set('ShowOnlyCurrentPersonnel', request.showOnlyCurrentPersonnel);
    }
    if (request.showFuture !== null && request.showFuture !== undefined) {
      params = params.set('ShowFuture', request.showFuture);
    }
    if (request.showWorkPlan !== null && request.showWorkPlan !== undefined) {
      params = params.set('ShowFutureWorkPlansAdditionalTimes', request.showWorkPlan);
    }
    if (request.tripId !== null && request.tripId !== undefined) {
      params = params.set('TripId', request.tripId);
    }
    if (request.showAllInTripBased !== null && request.showAllInTripBased !== undefined) {
      params = params.set('ShowAllInTripBased', request.showAllInTripBased);
    }
    if (
      request.onlyPeopleWithWatchDuties !== null &&
      request.onlyPeopleWithWatchDuties !== undefined
    ) {
      params = params.set('OnlyPeopleWithWatchDuties', request.onlyPeopleWithWatchDuties);
    }
    if (currentFilter) {
      params = params.set('Page', currentFilter.pageIndex.toString());
      params = params.append('PageSize', currentFilter.pageSize.toString());
    }
    if (currentFilter?.searchTerm) {
      params = params.append('GlobalSearch', currentFilter.searchTerm);
    }
    if (currentFilter?.orderBy && currentFilter.ascDesc) {
      if (currentFilter.orderBy === 'Position') {
        params = params.append(`OrderBy[${COLUMN_TO_KEY_MAP.position}]`, currentFilter.ascDesc);
      } else {
        params = params.append(`OrderBy[${currentFilter.orderBy}]`, currentFilter.ascDesc);
      }
    }
    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      currentFilter,
      COLUMN_TO_KEY_MAP
    );

    return this.http.get<ApiResponse<IWorkOverviewMatrixRow[]>>(this.url, { params });
  }

  getWorkOverviewById(
    id: string,
    year: number,
    month: number,
    showFuture: boolean | null,
    showWorkPlan: boolean | null
  ): Observable<IWorkOverviewDetails> {
    let params = new HttpParams();

    if (year) {
      params = params.append('Year', year);
    }
    if (month) {
      params = params.append('Month', month);
    }
    if (showFuture !== null && showFuture !== undefined) {
      params = params.append('ShowFuture', showFuture);
    }
    if (showWorkPlan !== null && showWorkPlan !== undefined) {
      params = params.set('ShowFutureWorkPlansAdditionalTimes', showWorkPlan);
    }

    const url = `${this.url}/${id}`;
    return this.http.get<IWorkOverviewDetails>(url, { params });
  }

  getAvailablePersonnel(
    hasAssignedWorkSchedule?: boolean | null,
    isForWorkPlan?: boolean | null,
    from?: string,
    to?: string | null
  ): Observable<IPersonnelOnBoard[]> {
    let params = new HttpParams();
    if (from) {
      params = params.set('From', from);
    }
    if (to) {
      params = params.set('To', to);
    }
    if (hasAssignedWorkSchedule !== null && hasAssignedWorkSchedule !== undefined) {
      params = params.set('HasAssignedWorkSchedule', hasAssignedWorkSchedule);
    }
    if (isForWorkPlan !== null && isForWorkPlan !== undefined) {
      params = params.set('IsForWorkPlan', isForWorkPlan);
    }

    const url = `${this.url}/availableOnboardPersonnel`;
    return this.http.get<IPersonnelOnBoard[]>(url, { params });
  }

  getAlternativeWorkOverviewById(
    id: string,
    year: number,
    month: number
  ): Observable<IAlternativeWorkOverviewResponse> {
    const firstDay = this.datePipe.transform(new Date(+year, +month, 1), DEFAULT_DATE_FORMAT);
    const lastDay = this.datePipe.transform(new Date(+year, +month + 1, 0), DEFAULT_DATE_FORMAT);
    const url = `${this.url}/${id}/alternative?From=${firstDay}&To=${lastDay}`;
    return this.http.get<IAlternativeWorkOverviewResponse>(url);
  }

  getDailyCrewWorkOverview(
    request: IGetDailyCrewWorkOverviewRequest
  ): Observable<IDailyCrewWorkOverviewResult> {
    return this.http.post<IDailyCrewWorkOverviewResult>(`${this.url}/daily`, request, {
      headers: this.headers,
    });
  }

  printWorkOverviews(year: number, month: number): Observable<Blob> {
    const params = new HttpParams();
    const url = `${this.url}/print/pdf?Year=${year}&Month=${month}`;
    return this.http.get(url, { params, responseType: 'blob' });
  }

  printWorkOverviewById(id: string, year: number, month: number): Observable<Blob> {
    const params = new HttpParams();
    const url = `${this.url}/${id}/print/pdf?Year=${year}&Month=${month}`;
    return this.http.get(url, { params, responseType: 'blob' });
  }
}
